/**
 * D454 step 3: the screens of arrange mode. The mode bar takes the goal strip's place under the
 * header; the dock sits under the map in place of the producer sheet. The map's own part (ghost,
 * spot markers, link flows, the yard ring, picking) calls the store's `pickUp`, `hover`, `drop`
 * and `putBack`; everything here reads the same state.
 */
import type { JSX } from 'preact';
import { playSound } from '../juice/index.ts';
import { Button } from '../ui/Button.tsx';
import { Icon } from '../ui/Icon.tsx';
import { ARRANGE_KEY, arrangeBarText, changeText, dockView } from './arrange-view.ts';
import { arrangedWorld, idleContent, useIdle } from './store.ts';
import { iconFor } from './view.ts';

/** The mode bar: icon, two lines, Cancel and Done. Shown in place of the goal strip. */
export function ArrangeBar() {
  const held = useIdle((s) => s.arrange?.held ?? null);
  const on = useIdle((s) => !!s.arrange);
  if (!on) return null;
  const { title, sub } = arrangeBarText(idleContent, held);
  return (
    <div
      data-testid="arrange-bar"
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--line)',
      }}
    >
      <span style={{ flex: 'none', color: 'var(--accent)', display: 'flex' }}>
        <Icon name="move" size={22} />
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 2 }}>
        <span data-testid="arrange-title" style={{ font: '600 14px/1.25 var(--font-ui)' }}>
          {title}
        </span>
        <span
          data-testid="arrange-sub"
          style={{ font: '400 12px/1.3 var(--font-ui)', color: 'var(--ink-2)' }}
        >
          {sub}
        </span>
      </span>
      <Button
        variant="secondary"
        data-testid="arrange-cancel"
        style={{ minHeight: 44, padding: '0 12px', font: '600 14px/1 var(--font-ui)' }}
        onClick={() => {
          playSound('tap');
          useIdle.getState().cancelArrange();
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        data-testid="arrange-done"
        style={{ minHeight: 44, padding: '0 14px', font: '600 14px/1 var(--font-ui)' }}
        onClick={() => {
          playSound('tap');
          useIdle.getState().doneArrange();
        }}
      >
        Close
      </Button>
    </div>
  );
}

const swatch = (extra: JSX.CSSProperties): JSX.CSSProperties => ({
  flex: 'none',
  width: 18,
  height: 18,
  boxSizing: 'border-box',
  ...extra,
});

/** The one-time key: what the map's marks mean while arranging. */
function ArrangeKey() {
  const [free, link, yard] = ARRANGE_KEY.items();
  const items: Array<[string | undefined, JSX.CSSProperties]> = [
    [free, { border: '2px dashed var(--accent)', borderRadius: 4 }],
    [
      link,
      { height: 4, marginTop: 7, marginBottom: 7, background: 'var(--accent)', borderRadius: 2 },
    ],
    [yard, { border: '2px solid var(--accent)', borderRadius: '50%' }],
  ];
  return (
    <div data-testid="arrange-key" style={{ display: 'grid', gap: 8 }}>
      <span style={{ font: '400 13px/1.4 var(--font-ui)' }}>{ARRANGE_KEY.lead()}</span>
      <ul
        style={{
          display: 'grid',
          gap: 6,
          margin: 0,
          padding: 0,
          listStyle: 'none',
          font: '500 13px/1.3 var(--font-ui)',
        }}
      >
        {items.map(([label, look]) => (
          <li key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span aria-hidden="true" style={swatch(look)} />
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The dock under the map while arranging. */
export function ArrangeDock() {
  const world = useIdle((s) => s.world);
  const arrange = useIdle((s) => s.arrange);
  if (!world || !arrange) return null;
  const { held, target, check } = arrange;
  const view =
    held && target && check
      ? dockView(arrangedWorld(world, arrange), idleContent, held, target, check)
      : null;
  const blocked = view?.blocked ?? true;
  return (
    <div
      data-testid="arrange-dock"
      style={{
        position: 'relative',
        zIndex: 1,
        flex: 'none',
        boxSizing: 'border-box',
        display: 'grid',
        gap: 8,
        alignContent: 'start',
        padding: '10px var(--edge) calc(12px + env(safe-area-inset-bottom))',
        borderRadius: '12px 12px 0 0',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.12)',
        background: 'var(--bg)',
      }}
    >
      {view ? (
        <div style={{ display: 'grid', gap: 4 }} aria-live="polite">
          <span
            data-testid="dock-heading"
            style={{
              font: '600 var(--fs-name)/1.25 var(--font-ui)',
              color: view.blocked ? 'var(--red)' : 'var(--ink)',
            }}
          >
            {view.heading}
          </span>
          {view.rows
            ? view.rows.map((r) => (
                <div
                  key={r.producer}
                  data-testid="dock-row"
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <span
                    aria-hidden="true"
                    data-testid="dock-tile"
                    style={{
                      flex: 'none',
                      width: 32,
                      height: 32,
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--surface-2)',
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <Icon name={iconFor(r.producer)} size={20} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 1 }}>
                    {r.where && (
                      <span style={{ font: '500 13px/1.3 var(--font-ui)' }}>{r.where}</span>
                    )}
                    <span style={{ font: '400 12px/1.3 var(--font-ui)', color: 'var(--ink-2)' }}>
                      {r.why}
                    </span>
                  </span>
                  <span
                    data-testid="dock-change"
                    style={{
                      font: '700 13px/1 var(--font-num)',
                      color:
                        r.change > 0
                          ? 'var(--green)'
                          : r.change < 0
                            ? 'var(--red)'
                            : 'var(--ink-2)',
                    }}
                  >
                    {changeText(r.change)}
                  </span>
                </div>
              ))
            : view.lines.map((l) => (
                <span
                  key={l}
                  data-testid="dock-line"
                  style={{ font: '400 13px/1.4 var(--font-ui)', color: 'var(--ink)' }}
                >
                  {l}
                </span>
              ))}
        </div>
      ) : held ? (
        <span data-testid="dock-hint" style={{ font: '400 13px/1.4 var(--font-ui)' }}>
          {arrangeBarText(idleContent, held).sub}.
        </span>
      ) : arrange.key ? (
        <ArrangeKey />
      ) : (
        <span data-testid="dock-hint" style={{ font: '400 13px/1.4 var(--font-ui)' }}>
          Moving is free. Tap a plot to pick it up.
        </span>
      )}
      {held && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="secondary"
            full
            data-testid="arrange-put-back"
            onClick={() => useIdle.getState().putBack()}
          >
            Put it back
          </Button>
          <Button
            variant="primary"
            full
            data-testid="arrange-put-here"
            disabled={blocked}
            onClick={() => {
              const r = useIdle.getState().drop();
              playSound(r.ok ? 'tap' : 'error');
            }}
          >
            {view?.rows ? 'Swap them' : 'Put it here'}
          </Button>
        </div>
      )}
    </div>
  );
}
